/**
 * Handle Name Setting Modal Component
 * Displays only the handle name setting section (extracted from MyPageModal)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { localizedApiMessage } from '../lib/api-error-message';
import { apiClient } from '../lib/api-client';

interface HandleNameSettingModalProps {
    isOpen: boolean;
    onClose: () => void;
    username: string;
    handleName?: string | null;
    onHandleNameUpdate?: (newHandleName: string) => void;
}

export default function HandleNameSettingModal({
    isOpen,
    onClose,
    username,
    handleName,
    onHandleNameUpdate,
}: HandleNameSettingModalProps) {
    const { t } = useTranslation('common');
    const [newHandleName, setNewHandleName] = useState(handleName || username);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Update local state when handleName prop changes
    useEffect(() => {
        setNewHandleName(handleName || username);
    }, [handleName, username]);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setNewHandleName(handleName || username);
            setError(null);
        }
    }, [isOpen, handleName, username]);

    if (!isOpen) return null;

    const handleSaveHandleName = async () => {
        if (!newHandleName.trim()) {
            setError(t('mypage.handleName.required'));
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            const response = await apiClient.updateHandleName(newHandleName.trim());
            onHandleNameUpdate?.(response.display_name);
            onClose();
        } catch (err) {
            console.error('Failed to update handle name:', err);
            // NGワードで拒否された場合は該当語を示す（それ以外は従来の汎用メッセージ）
            const message = localizedApiMessage(err, 'mypage.handleName.updateError');
            setError(t(message.key, message.values));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white w-full max-w-md overflow-hidden flex flex-col mx-4">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <svg width="24" height="24" fill="currentColor" className="text-pink-500" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                        {t('mypage.handleName.title')}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="flex flex-col gap-4">
                        <div className="text-sm text-gray-600">
                            {t('mypage.handleName.description')}
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="text-sm text-gray-600 font-semibold">
                                {t('mypage.handleName.label')}
                            </label>
                            <input
                                type="text"
                                value={newHandleName}
                                onChange={(e) => setNewHandleName(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 text-gray-900 font-semibold focus:outline-none focus:border-pink-500"
                                autoFocus
                                maxLength={50}
                                disabled={isSaving}
                                placeholder={t('mypage.handleName.placeholder')}
                            />
                        </div>
                        {error && (
                            <div className="text-red-500 text-sm">{error}</div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-6 py-2 bg-white border-2 border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        {t('mypage.handleName.cancel')}
                    </button>
                    <button
                        onClick={handleSaveHandleName}
                        disabled={isSaving}
                        className="px-6 py-2 bg-pink-500 text-white font-semibold hover:bg-pink-600 transition-colors disabled:opacity-50"
                    >
                        {isSaving ? t('mypage.handleName.saving') : t('mypage.handleName.save')}
                    </button>
                </div>
            </div>
        </div>
    );
}
